/**
 * Global Error Handler
 * Handles unhandled errors and promise rejections across the application
 */

import safeToast from "./safeToast";
import { initializeToastErrorSuppression } from "./toastErrorSuppressor";

/**
 * Check if error is a toast-related error that should be suppressed
 */
const isToastError = (error) => {
  if (!error) return false;
  
  const errorMessage = error.message || error.toString() || "";
  const errorStack = error.stack || "";
  
  return (
    errorMessage.includes("removalReason") ||
    errorMessage.includes("Cannot set properties of undefined") ||
    errorStack.includes("closeToast") ||
    errorStack.includes("removalReason")
  );
};

/**
 * Handle unhandled errors
 */
export const handleError = (error, errorInfo = null) => {
  // Ignore undefined/null errors (often from React DevTools or hooks)
  if (!error && (!errorInfo || (!errorInfo.filename && !errorInfo.lineno && !errorInfo.colno))) {
    // This is likely a false positive from React DevTools or hooks
    if (process.env.NODE_ENV === "development") {
      // Only log in development, don't show to user
      return;
    }
    return;
  }

  // Suppress toast-related errors silently
  if (isToastError(error)) {
    // Silently handle toast errors - don't show to user
    if (process.env.NODE_ENV === "development") {
      console.warn("Toast error suppressed:", error?.message);
    }
    return;
  }

  // Only log and show errors that are actually valid
  if (error || (errorInfo && (errorInfo.filename || errorInfo.lineno || errorInfo.colno))) {
    console.error("Global error handler:", error, errorInfo);

    // Show user-friendly message for non-toast errors
    try {
      safeToast.error("An unresolved problem was found. Please try again or refresh the page.", {
        autoClose: 5000,
      });
    } catch (toastError) {
      console.error("Failed to show error toast:", toastError);
      // Just log to console - don't use alert
      console.error("An unresolved problem was found. Please try refreshing the page.");
    }
  }

  // You can also send error to logging service here
  // Example: logErrorToService(error, errorInfo);
};

/**
 * Handle unhandled promise rejections
 */
export const handleUnhandledRejection = (event) => {
  const error = event.reason;
  
  // Suppress toast-related errors silently
  if (isToastError(error)) {
    // Silently handle toast errors - don't show to user
    if (process.env.NODE_ENV === "development") {
      console.warn("Toast promise rejection suppressed:", error?.message);
    }
    event.preventDefault();
    return;
  }

  console.error("Unhandled promise rejection:", error);

  // Prevent default browser error handling
  event.preventDefault();

  // Show user-friendly message for non-toast errors
  try {
    safeToast.error("An unresolved problem was found. Please try again or refresh the page.", {
      autoClose: 5000,
    });
  } catch (toastError) {
    console.error("Failed to show error toast:", toastError);
    // Just log to console - don't use alert
    console.error("An unresolved problem was found. Please try refreshing the page.");
  }
};

/**
 * Initialize global error handlers
 */
export const initializeGlobalErrorHandlers = () => {
  // First, initialize toast error suppression to catch toast errors early
  initializeToastErrorSuppression();

  // Handle unhandled errors (toast errors are already suppressed)
  window.addEventListener("error", (event) => {
    // Filter out invalid errors (often from React DevTools installHook)
    const error = event.error || event.message;
    const errorInfo = {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    };

    // Skip if error is undefined and all errorInfo fields are undefined
    // This prevents false positives from React DevTools hooks
    if (!error && !errorInfo.filename && !errorInfo.lineno && !errorInfo.colno) {
      return;
    }

    // Skip errors from installHook.js (React DevTools)
    if (errorInfo.filename && errorInfo.filename.includes("installHook")) {
      return;
    }

    handleError(error, errorInfo);
  }, true); // Use capture phase

  // Handle unhandled promise rejections (toast errors are already suppressed)
  window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

  // Handle React errors (if using React Error Boundary, this is a backup)
  // Note: We're not overriding console.error globally as it can cause issues
  // Instead, errors are caught by ErrorBoundary and safeToast handles toast errors

  console.log("Global error handlers initialized");
};

/**
 * Cleanup global error handlers (useful for testing)
 */
export const cleanupGlobalErrorHandlers = () => {
  window.removeEventListener("error", handleError);
  window.removeEventListener("unhandledrejection", handleUnhandledRejection);
};

